import React, { useState } from 'react';
import { Handle, Position } from '@xyflow/react';
import { MdAdd, MdClose } from 'react-icons/md';
import type { ArtifactsPanelStyle, NodeDefinitionDto } from '@api/nodeDefinitions';
import type { ArtifactKind, ProcessArtifactDto } from '@api/processes';
import type { Port } from '../graphTypes';
import {
  maxOutputsFor,
  minOutputsFor,
} from './useProcessNodeActionHandlers';
import { OUTPUT_PORT_KIND_OPTIONS } from '../portKindOptions';
import ArtifactDownloadList from './ArtifactDownloadList';
import ArtifactPreview from '../runStatus/ArtifactPreview';
import SelectField, { type SelectFieldOption } from './SelectField';
import { EmptyState, EMPTY_STATE_SIZE_XS } from '@aibuild/ui';
import { ArtifactsStrip } from './nodeCommon.styled';
import {
  OutputArtifactsWrap,
  OutputRowAddBtn,
  OutputRowHeader,
  OutputRowInstr,
  OutputRowItem,
  OutputRowKindRow,
  OutputRowList,
  OutputRowNameInput,
  OutputRowNameRow,
  OutputRowPill,
  OutputRowRemoveBtn,
  OutputRowSectionHead,
} from './OutputRow.styled';

export interface OutputRowProps {
  readonly ports: readonly Port[];
  readonly nodeId: string;
  readonly readOnly?: boolean;
  readonly outputInstructions?: Record<string, string>;
  /**
   * Map of {@code portId} → produced artifacts for the latest run on
   * this node. When provided, each output declaration renders its
   * matching artifacts directly beneath it so the user can see what
   * came out of which output without scanning a separate panel.
   * Orphan artifacts (no {@code portId}, or referencing a removed
   * port) keep rendering in the top {@link ArtifactsPanel}.
   */
  readonly artifactsByPortId?: ReadonlyMap<string, readonly ProcessArtifactDto[]>;
  /**
   * Catalog-supplied panel variant for the per-output artifact section
   * — mirrors {@link ArtifactsPanelProps.variant} so the per-output
   * renderer matches the top panel. {@code PREVIEW} shows inline image
   * thumbnails / 3D viewers via {@link ArtifactPreview}; {@code LIST}
   * shows compact downloadable rows; {@code NONE} omits artifacts
   * entirely. Defaults to {@code PREVIEW} to keep the design's inline
   * thumbnail affordance for Task nodes.
   */
  readonly artifactsVariant?: ArtifactsPanelStyle;
  /**
   * Catalog definition for this node — used to source the add/remove
   * bounds for the variable output group (Task: 1..5). Optional for
   * older test fixtures; falls back to the legacy 1..5 defaults via
   * {@link minOutputsFor} / {@link maxOutputsFor}.
   */
  readonly definition?: NodeDefinitionDto;
  /**
   * Output data-type options for the per-port kind picker, supplied by the
   * backend for the node's selected agent (`NodeDefinitionDto.agentOutputKinds`).
   * The frontend renders whatever it's given — the per-agent allow-list (e.g.
   * CAM → File / G-code / Text / Number) lives entirely on the backend. Falls
   * back to the full static set only when the catalog omits the map (older
   * payloads / non-agent nodes / test fixtures).
   */
  readonly kindOptions?: readonly SelectFieldOption[];
  /**
   * Whether each output renders a free-text "instructions" field. On for
   * Task (the agent consumes them); off for Manual, which keeps a single
   * node-level instruction and would otherwise show a field that never
   * persists. Defaults to {@code true} for older callers / fixtures.
   */
  readonly showInstructions?: boolean;
  readonly onAdd?: () => void;
  readonly onRemove?: (portId: string) => void;
  readonly onRename?: (portId: string, label: string) => void;
  readonly onUpdateInstruction?: (portId: string, text: string) => void;
  /**
   * Change the artifact kind for a single output port. Wired by
   * {@code useProcessNodeViewModel} on Task nodes (variable output
   * group); omitted on nodes whose output kinds are catalog-fixed
   * (image-gen, 3d-gen) — when undefined, the per-port kind picker
   * is rendered read-only as a static label.
   */
  readonly onUpdatePortType?: (portId: string, kind: ArtifactKind) => void;
}

const CHEVRON_SVG = (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

interface OutputPortFieldsProps {
  readonly p: Port;
  readonly index: number;
  readonly nodeId: string;
  readonly readOnly?: boolean;
  readonly outputInstructions?: Record<string, string>;
  readonly portArtifacts?: readonly ProcessArtifactDto[];
  readonly artifactsVariant?: ArtifactsPanelStyle;
  readonly canRemove: boolean;
  readonly kindOptions: readonly SelectFieldOption[];
  readonly showInstructions: boolean;
  readonly onRemove?: (portId: string) => void;
  readonly onRename?: (portId: string, label: string) => void;
  readonly onUpdateInstruction?: (portId: string, text: string) => void;
  readonly onUpdatePortType?: (portId: string, kind: ArtifactKind) => void;
}

/**
 * Renders the artifact set for a single output port using the same
 * variant as the catalog-driven top {@link ArtifactsPanel} — that way
 * a Task node (PREVIEW variant) gets inline image thumbnails and 3D
 * viewers under each output instead of a bare download link, matching
 * the affordance the top panel used to have before per-output
 * grouping. Returns {@code null} when there are no artifacts to show
 * or the variant is {@code NONE}.
 */
const OutputPortArtifacts: React.FC<{
  readonly artifacts: readonly ProcessArtifactDto[];
  readonly variant: ArtifactsPanelStyle;
  readonly nodeId: string;
  readonly portId: string;
}> = ({ artifacts, variant, nodeId, portId }) => {
  if (variant === 'NONE' || artifacts.length === 0) return null;
  const testId = `process-node__output-artifacts-${nodeId}-${portId}`;
  if (variant === 'PREVIEW') {
    return (
      <OutputArtifactsWrap>
        <ArtifactsStrip data-testid={testId}>
          {artifacts.map((a) => (
            <ArtifactPreview
              key={a.artifactId}
              artifact={a}
              dataTestId={`${testId}__preview-${a.artifactId}`}
            />
          ))}
        </ArtifactsStrip>
      </OutputArtifactsWrap>
    );
  }
  return (
    <OutputArtifactsWrap>
      <ArtifactDownloadList artifacts={artifacts} dataTestId={testId} />
    </OutputArtifactsWrap>
  );
};

const OutputPortFields: React.FC<OutputPortFieldsProps> = ({
  p,
  index,
  nodeId,
  readOnly,
  outputInstructions,
  portArtifacts,
  artifactsVariant = 'PREVIEW',
  canRemove,
  kindOptions,
  showInstructions,
  onRemove,
  onRename,
  onUpdateInstruction,
  onUpdatePortType,
}) => {
  const placeholderName = `output_${index + 1}`;
  const [name, setName] = useState(() => p.label || placeholderName);
  const [instr, setInstr] = useState(
    () => outputInstructions?.[p.portId] ?? '',
  );
  const kindEditable = !readOnly && !!onUpdatePortType;
  const kindTestId = `process-node__output-kind-${nodeId}-${index}`;

  return (
    <OutputRowItem>
      <OutputRowNameRow>
        {canRemove && (
          <OutputRowRemoveBtn
            type="button"
            onClick={() => onRemove?.(p.portId)}
            title="Remove output"
            aria-label="Remove output"
            data-testid={`process-node__remove-output-${nodeId}-${index}`}
          >
            <MdClose size={14} />
          </OutputRowRemoveBtn>
        )}
        <OutputRowNameInput
          value={name}
          placeholder={placeholderName}
          readOnly={readOnly}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => onRename?.(p.portId, name)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          }}
          data-testid={`process-node__output-name-${nodeId}-${index}`}
        />
        <OutputRowPill
          data-testid={
            index === 0
              ? `process-node__handle-out-${nodeId}`
              : `process-node__handle-out-${nodeId}-${index}`
          }
        >
          {CHEVRON_SVG}
          <Handle
            type="source"
            position={Position.Right}
            id={p.portId}
          />
        </OutputRowPill>
      </OutputRowNameRow>
      <OutputRowKindRow>
        <SelectField
          label="Data Type"
          ariaLabel="Output data type"
          options={kindOptions}
          value={p.portType}
          onChange={(next) =>
            onUpdatePortType?.(p.portId, next as ArtifactKind)
          }
          readOnly={!kindEditable}
          testId={kindTestId}
        />
      </OutputRowKindRow>
      {showInstructions && (
        <OutputRowInstr
          value={instr}
          placeholder="Instructions for this output…"
          readOnly={readOnly}
          rows={2}
          onChange={(e) => setInstr(e.target.value)}
          onBlur={() => onUpdateInstruction?.(p.portId, instr)}
          data-testid={`process-node__output-instr-${nodeId}-${index}`}
        />
      )}
      {portArtifacts && portArtifacts.length > 0 ? (
        <OutputPortArtifacts
          artifacts={portArtifacts}
          variant={artifactsVariant}
          nodeId={nodeId}
          portId={p.portId}
        />
      ) : null}
    </OutputRowItem>
  );
};

const OutputRow: React.FC<OutputRowProps> = ({
  ports,
  nodeId,
  readOnly,
  outputInstructions,
  artifactsByPortId,
  artifactsVariant,
  definition,
  kindOptions = OUTPUT_PORT_KIND_OPTIONS,
  showInstructions = true,
  onAdd,
  onRemove,
  onRename,
  onUpdateInstruction,
  onUpdatePortType,
}) => {
  const canAdd = !readOnly && ports.length < maxOutputsFor(definition);
  const canRemove = !readOnly && ports.length > minOutputsFor(definition);

  return (
    <div className="nodrag">
      <OutputRowHeader>
        <OutputRowSectionHead>
          Outputs &middot; {ports.length}
        </OutputRowSectionHead>
        {canAdd && (
          <OutputRowAddBtn
            type="button"
            onClick={onAdd}
            title="Add output"
            aria-label="Add output"
            data-testid={`process-node__add-output-${nodeId}`}
          >
            <MdAdd size={16} />
          </OutputRowAddBtn>
        )}
      </OutputRowHeader>
      {ports.length === 0 ? (
        <EmptyState
          size={EMPTY_STATE_SIZE_XS}
          title="No outputs yet — press + to add one."
          dataTestId={`process-node__outputs-empty-${nodeId}`}
        />
      ) : (
        <OutputRowList>
          {ports.map((p, i) => (
            <OutputPortFields
              key={`${p.portId}:${p.label ?? ''}:${p.portType}:${outputInstructions?.[p.portId] ?? ''}`}
              p={p}
              index={i}
              nodeId={nodeId}
              readOnly={readOnly}
              outputInstructions={outputInstructions}
              portArtifacts={artifactsByPortId?.get(p.portId)}
              artifactsVariant={artifactsVariant}
              canRemove={canRemove}
              kindOptions={kindOptions}
              showInstructions={showInstructions}
              onRemove={onRemove}
              onRename={onRename}
              onUpdateInstruction={onUpdateInstruction}
              onUpdatePortType={onUpdatePortType}
            />
          ))}
        </OutputRowList>
      )}
    </div>
  );
};

export default OutputRow;
